import { Injectable, inject } from '@angular/core';
import { map } from 'rxjs/operators';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { CompanyAccess } from 'src/model/companyAccess.model';

export enum AccessType {
  Essential,
  Normal,
  Temporary,
  OnDuty
}

export interface AccessResponse {
  companyIds: number[];
  username: string;
  message?: string;
}

export type AccessRequest = AccessResponse & { priority: number }

@Injectable({
  providedIn: 'root'
})
class AdminAccessAdapter {
  adapt(item: any): CompanyAccess {
    return new CompanyAccess(item.companyCode.trim(), item.companyId, item.priority, item.username);
  }

  adaptAccessRequest(item: any): AccessRequest {
    return {
      companyIds: item.companyIds,
      username: item.username,
      priority: item.priority,
      message: item.message
    }
  }

  adaptAccessResponse(item: any): AccessResponse {
    return {
      companyIds: item.companyIds,
      username: item.username,
      message: item.message
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class AdminAccessService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(AdminAccessAdapter);
  private enums = inject(Enums);
  private sharedService = inject(SharedService);

  readonly maxRetryAttempts = 3;

  getCompaniesAccess(compID?: number): Promise<CompanyAccess[]> {
    return new Promise<CompanyAccess[]>((resolve, reject) => {
      this.api.get<CompanyAccess[]>(this.constructApi.getCompaniesAccess(compID)).pipe(
        map(data => data.map(item => this.adapter.adapt(item)))
      ).subscribe({
        next: (data) => {
          resolve(data);
        },
        error: (error) => {
          this.api.handleError(error);
          reject(error);
        }
      });
    });
  }

  revokeAccess(username: string, companies: number[]): Promise<AccessResponse> {
    const data = { companyIds: companies };
    return new Promise<AccessResponse>((resolve, reject) => {
      this.api.delete(this.constructApi.getRevokeUserCompanyAccess(username), data).subscribe({
        next: response => resolve(this.adapter.adaptAccessRequest(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to grant access: ', err);
          reject(err);
        }
      })
    });
  }

  putGrantAccessRequest(username: string, priority: number, companies: number[]) {
    const data = { companyIds: companies };
    return new Promise<AccessRequest>((resolve, reject) => {
      this.api.put<AccessRequest>(this.constructApi.getGrantUserCompanyAccess(username, priority), data).subscribe({
        next: response => resolve(this.adapter.adaptAccessRequest(response)),
        error: err => {
          this.api.handleError(err);
          console.error('Failed to grant access: ', err);
          reject(err);
        }
      })
    });
  }

  deduceCompanyAccessMap(companiesAccessToChange: CompanyAccess[]): Map<number, number[]> {
    return companiesAccessToChange.reduce((result: Map<number, number[]>, { CompanyId, Priority }) => {
      result.set(Priority, [...(result.get(Priority) || []), CompanyId]);
      return result;
    }, new Map<number, number[]>());
  }

  // Other shared functions:

  isEssentialPriority(priority: number) {
    return [this.enums.FIRST_PRIORITY, this.enums.SECOND_PRIORITY, this.enums.THIRD_PRIORITY].includes(priority);
  }

  getAccessType(priority: number, checkEssential = false) {
    switch (priority % 10) {
      case 0:
        if (checkEssential && this.isEssentialPriority(priority))
          return AccessType.Essential;
        return AccessType.Normal;
      case 1:
        if (priority === this.enums.ON_DUTY_PRIORITY)
          return AccessType.OnDuty;
        else return AccessType.Temporary;
    }
  }

  async grantAccessWithRetry(username: string, priority: number, companies: number[], granted: AccessRequest = {} as AccessRequest, retryAttempt = 0): Promise<AccessRequest> {
    const comp = companies.filter(c => !granted?.companyIds?.includes(c));
    const currentGranted = await this.putGrantAccessRequest(username, priority, comp);
    if (Object.keys(granted).length == 0)
      granted = currentGranted;
    else
      granted.companyIds = granted.companyIds.concat(currentGranted.companyIds ?? []);

    if (retryAttempt < this.maxRetryAttempts && granted?.companyIds.length < companies.length) {
      await this.sharedService.sleep(500);
      await this.grantAccessWithRetry(username, priority, comp, granted, retryAttempt + 1);
    }

    return granted;
  }

  async revokeAccessWithRetry(username: string, companies: number[], revoked: AccessResponse = {} as AccessResponse, retryAttempt = 0): Promise<AccessResponse> {
    const comp = companies.filter(c => !revoked?.companyIds?.includes(c));
    const currentRevoked = await this.revokeAccess(username, comp);
    if (Object.keys(revoked).length == 0)
      revoked = currentRevoked;
    else
      revoked.companyIds = revoked.companyIds.concat(currentRevoked.companyIds ?? []);

    if (retryAttempt < this.maxRetryAttempts && revoked?.companyIds.length < companies.length) {
      await this.sharedService.sleep(500);
      await this.revokeAccessWithRetry(username, comp, revoked, retryAttempt + 1);
    }

    return revoked;
  }

}
